import { get } from '@/infra/rest';
import { useAgentData, useSelectedReviewResponse } from '@/modules/agent/states';
import { CustomControlsType, useCustomizeControlsStore } from '@/modules/agent/states/cusomize-controls';
import { downloadFileWithCheck, getFilesUsedForSession } from '@/modules/agent/utils';
import { checkAndRefreshUrl } from '@/modules/agent/utils/refreshUrl';
import { AgentSessionStepType } from '@/modules/sessions/types';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from '@/shared/components/drawer';
import { addNotification } from '@/shared/states/notification';
import { Input, Spinner } from '@nextui-org/react';
import { File } from 'lucide-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ZFileName from '../custom/file-upload/FileName';
import useDrawerStore from './useDrawerStore';

interface Params {
  id: string;
}

const ViewSessionsFilesDrawer: React.FC = () => {
  const isOpen = useDrawerStore((state) => state.isOpen);
  const onClose = useDrawerStore((state) => state.closeDrawer);
  const controlId = useDrawerStore((state) => state.controlId);

  const { id } = useParams<keyof Params>();
  const agentData = useAgentData(id as string);

  const [filesMap, setFilesMap] = useState<Map<string, string>>(new Map())
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [customControlsData, setCustomControlsData] = useState<CustomControlsType[]>([]);

  const { id: selectedId = "", data: selectedData } = useSelectedReviewResponse(id as string);

  const ifSessionHasEvidenceMapping = useCustomizeControlsStore().isEvidenceMapping

  const showControlSpecificFiles = ifSessionHasEvidenceMapping && controlId && selectedId;

  useEffect(() => {
    setSearchTerm("")
  }, [isOpen])

  useEffect(() => {
    if (agentData && isOpen) {
      if (ifSessionHasEvidenceMapping) {
        const fetchEvidenceMappingControls = async () => {
          const url = (agentData as any)?.stepData.find(
            (x: any) => x.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING || x.type === AgentSessionStepType.CONTROL_SELECTION
          ).data.url;
          const validSignedUrl = await checkAndRefreshUrl(url);

          let customizeControlJson = await get<CustomControlsType[]>({
            url: validSignedUrl,
            isAuthRequired: false,
          });

          setCustomControlsData(customizeControlJson);

          const fileUrlMap = new Map();
          customizeControlJson.forEach((control: any) => {
            control.file_names?.forEach((fileName: string, idx: number) => {
              const url = control.source_urls[idx];
              fileUrlMap.set(fileName, url ?? "");
            });
          });

          setFilesMap(fileUrlMap)
          setIsLoading(false)
        }

        fetchEvidenceMappingControls()
      } else {
        setFilesMap(getFilesUsedForSession(agentData))
        setIsLoading(false)
      }
    }
  }, [ifSessionHasEvidenceMapping, id, agentData?.sessionData.updated_at, isOpen])

  if (!isOpen) {
    return null;
  }

  let selectedControlFilesSet = new Set<string>();

  if (showControlSpecificFiles) {
    const selectedControlId = selectedData?.find(x => x.key === "control_id")?.value;
    const selectedControlDetails = customControlsData.find((control: CustomControlsType) => control.control_id === selectedControlId);

    if (selectedControlDetails) {
      selectedControlFilesSet = new Set(selectedControlDetails.file_names)
    }
  }

  const filesToShow = showControlSpecificFiles
    ? Array.from(filesMap.entries()).filter(([fileName]) => selectedControlFilesSet.has(fileName))
    : Array.from(filesMap.entries());

  const filteredFiles = filesToShow.filter(([fileName]) =>
    fileName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const showErrorToast = (msg: string) => {
    addNotification({
      message: msg,
      type: "error",
    });
  };

  return (
    <Drawer isOpen={isOpen} placement={"right" as any} onClose={onClose}>
      <DrawerContent className="drawer-content">
        <DrawerHeader className='border-b-1 flex items-center'>
          <h3>Uploaded Files</h3>
        </DrawerHeader>
        <DrawerBody className='drawer-body py-4 px-6 gap-0 flex flex-col max-h-[-webkit-fill-available] mb-12'>
          <Input
            placeholder="Search using filename"
            value={searchTerm}
            onChange={handleSearchChange}
            className='mb-2'
          />
          <div className='overflow-y-auto grow flex flex-col'>
            <div>
              {filteredFiles.length > 0 ? (
                filteredFiles.map(([fileName, url]) => (
                  <div
                    className="flex gap-2 items-center mt-0 p-3"
                    key={fileName}
                  >
                    <button
                      onClick={() => downloadFileWithCheck(fileName, url, showErrorToast)}
                      className="flex items-center gap-2 text-green-600 cursor-pointer bg-transparent border-none p-0"
                      type="button"
                    >
                      <File size={16} color="#22c55e" className="min-w-4" />
                      <ZFileName filename={fileName} className='hover:underline' />
                    </button>
                  </div>
                ))
              ) : (
                <div className="flex items-center mt-0 p-3 text-center">
                  {isLoading ? <Spinner className='m-auto' /> : <p>No files found.</p>}
                </div>
              )}
            </div>
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ViewSessionsFilesDrawer;
